import { useState, useEffect } from 'react';
import React from 'react';
import { ErrorBoundary } from "react-error-boundary";
import { RootState, useAppSelector } from '../../../../state/state';
import { doc, getDoc, updateDoc, collection, getDocs, deleteDoc, setDoc, query, orderBy, limit } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes, deleteObject } from "firebase/storage";
import { db, storage } from '../../../../Config/firebase';
import { toast } from 'react-toastify';
import { 
    Button, 
    CircularProgress, 
    IconButton, 
    TextField,
    Tabs,
    Tab,
    Badge
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import StorefrontIcon from '@mui/icons-material/Storefront';
import InventoryIcon from '@mui/icons-material/Inventory';
import ProductFormModal from './components/ProductFormModal';

import ReceiptIcon from '@mui/icons-material/Receipt';
import OrdersTab from './components/OrdersTab';
import ProductsTab from './components/ProductsTab';

import {
    StorefrontData,
    ProductData,
    ProductOrder
} from './ProductTypes';
import { selectEffectiveUID } from '../../../../state/UserStatesV2/userSliceV2';


const STOREFRONT_TAB_KEY = 'storefrontActiveTab';

const StorefrontContent = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [storeDetails, setStoreDetails] = useState<StorefrontData>({
        name: "",
        description: "",
        settlementAccountId: 0,
        termsAccepted: false,
        GSTIN: ""
    });
    const [promoImageFile, setPromoImageFile] = useState<File | null>(null);
    const [promoImagePreview, setPromoImagePreview] = useState<string | null>(null);
    const [promoImageSizeExceeded, setPromoImageSizeExceeded] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState(() => {
        const savedTab = localStorage.getItem(STOREFRONT_TAB_KEY);
        return savedTab ? parseInt(savedTab, 10) : 0;
    });
    const [products, setProducts] = useState<ProductData[]>([]);
    const [selectedProduct, setSelectedProduct] = useState<ProductData | null>(null);
    const [isProductModalOpen, setIsProductModalOpen] = useState(false);
    const [orders, setOrders] = useState<ProductOrder[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    // Get provider ID from Redux state
    const providerId = useAppSelector(selectEffectiveUID);

    // Fetch storefront data from Firestore
    useEffect(() => {
        const fetchStorefrontData = async () => {
            if (!providerId) return;
            
            setIsLoading(true);
            try {
                const providerDoc = await getDoc(doc(db, 'ServiceProviders', providerId));
                if (providerDoc.exists()) {
                    const data = providerDoc.data();
                    if (data.storefront) {
                        setStoreDetails({
                            ...data.storefront,
                            GSTIN: data.GSTIN || ''
                        });
                    }
                }
            } catch (error) {
                toast.error('Failed to fetch storefront data');
            } finally {
                setIsLoading(false);
            }
        };

        fetchStorefrontData();
    }, [providerId]);

    // Fetch products
    useEffect(() => {
        fetchProducts();
    }, [providerId]);

    const fetchProducts = async () => {
        if (!providerId) return;
        setIsLoading(true);

        try {
            const productsCollection = collection(db, 'ServiceProviders', providerId, 'Products');
            const productsSnapshot = await getDocs(productsCollection);

            const productsList = productsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as ProductData[];

            setProducts(productsList);
        } catch (error) {
            toast.error('Failed to fetch products');
            console.error('Error fetching products:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePromoImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files ? event.target.files[0] : null;
        setPromoImageSizeExceeded(false);
        
        if (file) {
            if (file.size > 5 * 1024 * 1024) { // 5MB limit
                setPromoImageSizeExceeded(true);
                return;
            }
            setPromoImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPromoImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handlePromoImageUpload = async (): Promise<string> => {
        if (promoImageFile) {
            const storageRef = ref(storage, `storefronts/${providerId}/promoImage/${promoImageFile.name}`);
            await uploadBytes(storageRef, promoImageFile);
            return getDownloadURL(storageRef);
        }
        return storeDetails.promoImageUrl || '';
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        
        try {
            let promoImageUrl = storeDetails.promoImageUrl;
            if (promoImageFile) {
                promoImageUrl = await handlePromoImageUpload();
            }

            const updatedStoreDetails = {
                ...storeDetails,
                promoImageUrl
            };

            await updateDoc(doc(db, 'ServiceProviders', providerId), {
                storefront: updatedStoreDetails,
                GSTIN: storeDetails.GSTIN
            });
            
            setStoreDetails(updatedStoreDetails);
            toast.success('Store details updated successfully');
            setIsModalOpen(false);
        } catch (error) {
            toast.error('Failed to update store details');
        } finally {
            setIsLoading(false);
        }
    };

    const handleProductImageUpload = async (files: File[]): Promise<string[]> => {
        if (!files.length) return [];

        const uploadPromises = files.map(async (file) => {
            const timestamp = Date.now();
            const fileName = `${file.name.split('.')[0]}_${timestamp}.${file.name.split('.').pop()}`;
            const storageRef = ref(storage, `products/${providerId}/${fileName}`);
            await uploadBytes(storageRef, file);
            return getDownloadURL(storageRef);
        });

        return Promise.all(uploadPromises);
    };

    const deleteImageFromStorage = async (imageUrl: string) => {
        try {
            const imageRef = ref(storage, decodeURIComponent(imageUrl.split('?')[0].split('/o/')[1]));
            await deleteObject(imageRef);
        } catch (error) {
            console.error('Error deleting image:', error);
            // Don't throw error as this is not critical
        }
    };

    const handleProductSubmit = async (productData: Partial<ProductData>, imageFiles: File[]) => {
        setIsLoading(true);
        try {
            let finalProductImages: string[] = [];
            
            if (selectedProduct) {
                // For existing product updates
                const existingImages = selectedProduct.productImages || [];
                
                // Filter out data URLs from existing images
                const keepExistingImages = productData.productImages?.filter(img => 
                    !img.startsWith('data:')
                ) || [];

                // Upload new images
                const newImageUrls = await handleProductImageUpload(imageFiles);
                
                // Combine kept existing images with new uploads
                finalProductImages = [
                    ...keepExistingImages,
                    ...newImageUrls
                ];

                // Delete removed images from storage
                const removedImages = existingImages.filter(
                    oldUrl => !keepExistingImages.includes(oldUrl)
                );
                await Promise.all(removedImages.map(deleteImageFromStorage));

            } else {
                // For new product creation
                if (imageFiles.length > 0) {
                    finalProductImages = await handleProductImageUpload(imageFiles);
                }
            }

            // Create or get document references
            let productRef;
            let offerRef;
            
            if (selectedProduct?.id) {
                productRef = doc(db, 'ServiceProviders', providerId, 'Products', selectedProduct.id);
                offerRef = doc(db, 'ServiceProviders', providerId, 'Offers', selectedProduct.id);
            } else {
                const productsCollectionRef = collection(db, 'ServiceProviders', providerId, 'Products');
                const newDocRef = doc(productsCollectionRef);
                productRef = newDocRef;
                offerRef = doc(db, 'ServiceProviders', providerId, 'Offers', newDocRef.id);
            }
            // Prepare product data
            const productToSave = {
                ...productData,
                productImages: finalProductImages,
                productRef,
                offerRef,
                updatedAt: new Date().toISOString()
            };

            // Save to database
            if (selectedProduct?.id) {
                await updateDoc(productRef, productToSave);
            } else {
                await setDoc(productRef, {
                    ...productToSave,
                    createdAt: new Date().toISOString()
                });
            }

            fetchProducts();
            setIsProductModalOpen(false);
            toast.success(selectedProduct ? 'Product updated successfully' : 'Product created successfully');
        } catch (error) {
            console.error('Error saving product:', error);
            toast.error('Failed to save product');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteProduct = async (productId: string) => {
        if (!window.confirm('Are you sure you want to delete this product?')) return;

        try {
            // Get product data to clean up images
            const productDoc = await getDoc(doc(db, 'ServiceProviders', providerId, 'Products', productId));
            if (productDoc.exists()) {
                const productData = productDoc.data() as ProductData;
                
                // Delete all product images from storage
                if (productData.productImages) {
                    await Promise.all(productData.productImages.map(deleteImageFromStorage));
                }
            }

            // Delete the product document
            await deleteDoc(doc(db, 'ServiceProviders', providerId, 'Products', productId));
            toast.success('Product deleted successfully');
            fetchProducts();
        } catch (error) {
            console.error('Error deleting product:', error);
            toast.error('Failed to delete product');
        }
    };

    const handleToggleLiveStatus = async (product: ProductData) => {
        try {
            await updateDoc(doc(db, 'ServiceProviders', providerId, 'Products', product.id), {
                isLive: !product.isLive
            });
            toast.success(`Product ${product.isLive ? 'disabled' : 'enabled'} successfully`);
            fetchProducts();
        } catch (error) {
            toast.error('Failed to update product status');
        }
    };

    const handleEditProduct = (product: ProductData) => {
        setSelectedProduct({
            ...product,
        });
        setIsProductModalOpen(true);
    };

    const handleAddNewProduct = () => {
        setSelectedProduct(null);
        // setProductImages([]);
        // setProductImagePreviews([]);
        setIsProductModalOpen(true);
    };

    const handleCloseProductModal = () => {
        setIsProductModalOpen(false);
        setSelectedProduct(null);
        // setProductImages([]);
        // setProductImagePreviews([]);
    };

    // Keep the orders fetching logic
    useEffect(() => {
        if (activeTab === 2) {
            fetchOrders();
        }
    }, [activeTab, providerId]);

    const fetchOrders = async () => {
        if (!providerId) return;
        setIsLoading(true);

        try {
            const ordersRef = collection(db, 'ServiceProviders', providerId, 'ProductOrders');
            const q = query(
                ordersRef,
                orderBy('createdAt', 'desc'),
                limit(1000)
            );
            const snapshot = await getDocs(q);
            const ordersData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as ProductOrder[];
            setOrders(ordersData);
        } catch (error) {
            toast.error('Failed to fetch orders');
            console.error('Error fetching orders:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Update localStorage when tab changes
    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
        localStorage.setItem(STOREFRONT_TAB_KEY, newValue.toString());
    };

    const updateOrderInState = (orderId: string, updates: Partial<ProductOrder>) => {
        setOrders(currentOrders => 
            currentOrders.map(order => 
                order.id === orderId 
                    ? { ...order, ...updates }
                    : order
            )
        );
    };
    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center">
                            <StorefrontIcon className="h-8 w-8 text-blue-600" />
                            <h1 className="ml-2 text-xl font-semibold text-gray-900">Store Manager</h1>
                        </div>
                    </div>
                    <Tabs 
                        value={activeTab} 
                        onChange={handleTabChange}
                        className="border-b border-gray-200"
                    >
                        <Tab 
                            icon={<StorefrontIcon className="mr-2" />} 
                            label="Store Details" 
                            className="text-gray-600"
                        />
                        <Tab 
                            icon={<InventoryIcon className="mr-2" />} 
                            label={
                                <Badge badgeContent={products.length} color="primary">
                                    Products
                                </Badge>
                            }
                            className="text-gray-600"
                        />
                        <Tab 
                            icon={<ReceiptIcon className="mr-2" />}
                            label={
                                <Badge badgeContent={orders.length} color="primary">
                                    Orders
                                </Badge>
                            }
                            className="text-gray-600"
                        />
                    </Tabs>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Tab Content */}
                {activeTab === 0 ? (
                    // Store Details Tab Content
                    <div className="bg-white rounded-lg shadow">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900">{storeDetails.name || 'Your Store'}</h2>
                                    <p className="text-gray-500 mt-1">{storeDetails.description || 'Add a description to your store'}</p>
                                </div>
                                <Button
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={() => setIsModalOpen(true)}
                                >
                                    Edit Store
                                </Button>
                            </div>

                            {storeDetails.promoImageUrl && (
                                <div className="mt-6">
                                    <h3 className="text-lg font-medium text-gray-900 mb-3">Promotional Banner</h3>
                                    <div className="relative rounded-lg overflow-hidden h-64">
                                        <img 
                                            src={storeDetails.promoImageUrl} 
                                            alt="Store promotion" 
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : activeTab === 1 ? (
                    // Products Tab Content
                    <ProductsTab
                        products={products}
                        onEditProduct={handleEditProduct}
                        onDeleteProduct={handleDeleteProduct}
                        onToggleLiveStatus={handleToggleLiveStatus}
                        onAddNewProduct={handleAddNewProduct}
                    />
                ) : (
                    // Orders Tab Content
                    <OrdersTab
                        orders={orders}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        handleChangePage={handleChangePage}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                        providerId={providerId}
                        updateOrderInState={updateOrderInState}
                    />
                )}
            </div>

            {/* Modals */}
            {isModalOpen && (
                // Store Details Modal
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold">Edit Store Details</h2>
                            <IconButton onClick={() => setIsModalOpen(false)}>
                                <CloseIcon />
                            </IconButton>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <TextField
                                label="Store Name"
                                fullWidth
                                value={storeDetails.name}
                                onChange={(e) => setStoreDetails({...storeDetails, name: e.target.value})}
                                size="small"
                            />

                            <TextField
                                label="Description"
                                fullWidth
                                multiline
                                rows={3}
                                value={storeDetails.description}
                                onChange={(e) => setStoreDetails({...storeDetails, description: e.target.value})}
                                size="small"
                            />

                            <TextField
                                label="GSTIN"
                                fullWidth
                                value={storeDetails.GSTIN}
                                onChange={(e) => setStoreDetails({...storeDetails, GSTIN: e.target.value})}
                                size="small"
                            />

                            <div>
                                <Button
                                    variant="outlined"
                                    component="label"
                                    fullWidth
                                    sx={{
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        color: '#1D4ED8',
                                        borderColor: '#1D4ED8',
                                        '&:hover': {
                                            backgroundColor: '#1D4ED8',
                                            color: "#FFFFFF"
                                        },
                                    }}
                                >
                                    Upload Promotional Image <span className="text-sm ml-2">(Max 5mb)</span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        onChange={handlePromoImageChange}
                                    />
                                </Button>
                                {promoImageSizeExceeded && (
                                    <p className="text-red-500 text-sm mt-1">
                                        File is too large, please select a file less than 5MB.
                                    </p>
                                )}
                                {promoImagePreview && (
                                    <div className="relative mt-2 w-32 h-32">
                                        <img
                                            src={promoImagePreview}
                                            alt="Promo preview"
                                            className="w-full h-full object-cover rounded"
                                        />
                                        <IconButton
                                            onClick={() => {
                                                setPromoImageFile(null);
                                                setPromoImagePreview(null);
                                            }}
                                            className="absolute top-1 right-1 bg-white"
                                            size="small"
                                        >
                                            <CloseIcon />
                                        </IconButton>
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-end space-x-2 mt-6">
                                <Button
                                    onClick={() => setIsModalOpen(false)}
                                    variant="outlined"
                                    disabled={isLoading}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={isLoading}
                                    className="bg-blue-600 hover:bg-blue-700"
                                >
                                    {isLoading ? (
                                        <CircularProgress size={24} sx={{ color: 'white' }} />
                                    ) : (
                                        'Save Changes'
                                    )}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Product Form Modal */}
            <ProductFormModal
                serviceProviderId={providerId}
                open={isProductModalOpen}
                onClose={handleCloseProductModal}
                onSubmit={handleProductSubmit}
                initialData={selectedProduct}
                isLoading={isLoading}
            />
        </div>
    );
};

const Storefront = () => (
    <ErrorBoundary
        fallback={<div className="p-4 text-red-500">Something went wrong. Please try again later.</div>}
    >
        <StorefrontContent />
    </ErrorBoundary>
);

export default Storefront; 